import React, { useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, ArrowsRightLeftIcon, FunnelIcon } from '@heroicons/react/24/outline';

interface Transaction {
    id: number;
    created_at: string;
    item_type?: string;
    crop_type: string;
    quantity_kg: number;
    direction: 'incoming' | 'outgoing' | 'internal';
    reason: string;
    quality_grade?: string;
    status?: string;
    notes?: string;
    facility_id: number;
    source_type?: string;
    destination_type?: string;
}

interface Props {
    transactions: Transaction[];
    onViewDetails: (transaction: Transaction) => void;
}

const InventoryTransactionsTable: React.FC<Props> = ({ transactions, onViewDetails }) => {
    const [filters, setFilters] = useState({
        direction: '',
        reason: '',
        cropType: '',
        itemType: ''
    });

    const getDirectionBadge = (direction: string, quantity: number) => {
        if (direction === 'incoming') {
            return (
                <div className="flex items-center space-x-2">
                    <div className="bg-green-100 p-1.5 rounded-full">
                        <ArrowDownIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm font-semibold text-green-700">+{quantity} kg</span>
                </div>
            );
        } else if (direction === 'internal') {
            return (
                <div className="flex items-center space-x-2">
                    <div className="bg-indigo-100 p-1.5 rounded-full">
                        <ArrowsRightLeftIcon className="h-4 w-4 text-indigo-600" />
                    </div>
                    <span className="text-sm font-semibold text-indigo-700">{quantity} kg</span>
                </div>
            );
        } else {
            return (
                <div className="flex items-center space-x-2">
                    <div className="bg-red-100 p-1.5 rounded-full">
                        <ArrowUpIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-sm font-semibold text-red-700">-{quantity} kg</span>
                </div>
            );
        }
    };

    const getReasonBadge = (reason: string) => {
        const reasonStyles: Record<string, string> = {
            harvest: 'bg-green-100 text-green-800',
            produce_reception: 'bg-green-100 text-green-800',
            fertilizer_receipt: 'bg-lime-100 text-lime-800',
            compost_produced: 'bg-emerald-100 text-emerald-800',
            sale: 'bg-blue-100 text-blue-800',
            issue_to_farmer: 'bg-cyan-100 text-cyan-800',
            compost_distribution: 'bg-indigo-100 text-indigo-800',
            donation: 'bg-purple-100 text-purple-800',
            damage: 'bg-red-100 text-red-800',
            spoilage: 'bg-orange-100 text-orange-800',
            transfer: 'bg-gray-100 text-gray-800'
        };

        const style = reasonStyles[reason] || 'bg-gray-100 text-gray-800';

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style}`}>
                {reason.replace(/_/g, ' ').replace(/\b\w/g, (s) => s.toUpperCase())}
            </span>
        );
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const filteredTransactions = transactions.filter(t => {
        if (filters.direction && t.direction !== filters.direction) return false;
        if (filters.reason && t.reason !== filters.reason) return false;
        if (filters.cropType && t.crop_type !== filters.cropType) return false;
        if (filters.itemType && (t.item_type || 'produce') !== filters.itemType) return false;
        return true;
    });

    const availableReasons = Array.from(new Set(transactions.map((t) => t.reason).filter(Boolean)));
    const availableItemTypes = Array.from(new Set(transactions.map((t) => t.item_type || 'produce')));
    const availableCropTypes = Array.from(new Set(transactions.map((t) => t.crop_type).filter(Boolean)));

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header with Filters */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Inventory Transactions</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {filteredTransactions.length} transactions recorded
                        </p>
                    </div>
                    <FunnelIcon className="h-5 w-5 text-gray-400" />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-3">
                    <select
                        value={filters.direction}
                        onChange={(e) => setFilters(prev => ({ ...prev, direction: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00A1DE] focus:border-transparent"
                    >
                        <option value="">All Directions</option>
                        <option value="incoming">Incoming</option>
                        <option value="outgoing">Outgoing</option>
                        <option value="internal">Internal</option>
                    </select>

                    <select
                        value={filters.reason}
                        onChange={(e) => setFilters(prev => ({ ...prev, reason: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00A1DE] focus:border-transparent"
                    >
                        <option value="">All Reasons</option>
                        {availableReasons.map((reason) => (
                            <option key={reason} value={reason}>
                                {reason.replace(/_/g, ' ').replace(/\b\w/g, (s) => s.toUpperCase())}
                            </option>
                        ))}
                    </select>

                    <select
                        value={filters.cropType}
                        onChange={(e) => setFilters(prev => ({ ...prev, cropType: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00A1DE] focus:border-transparent"
                    >
                        <option value="">All Crops</option>
                        {availableCropTypes.map((crop) => (
                            <option key={crop} value={crop}>{crop.charAt(0).toUpperCase() + crop.slice(1)}</option>
                        ))}
                    </select>

                    <select
                        value={filters.itemType}
                        onChange={(e) => setFilters(prev => ({ ...prev, itemType: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#00A1DE] focus:border-transparent"
                    >
                        <option value="">All Items</option>
                        {availableItemTypes.map((itemType) => (
                            <option key={itemType} value={itemType}>
                                {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Item Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Crop
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Quantity
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Direction
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Reason
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Flow
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTransactions.map((transaction) => (
                            <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatDate(transaction.created_at)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                                        {transaction.item_type || 'produce'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="text-sm font-medium text-gray-900">
                                            {transaction.crop_type.charAt(0).toUpperCase() + transaction.crop_type.slice(1)}
                                        </div>
                                        {transaction.quality_grade && (
                                            <span className="ml-2 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                                Grade {transaction.quality_grade}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getDirectionBadge(transaction.direction, transaction.quantity_kg)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${transaction.direction === 'incoming' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                        }`}>
                                        {transaction.direction === 'internal'
                                            ? 'Internal'
                                            : (transaction.direction.charAt(0).toUpperCase() + transaction.direction.slice(1))}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getReasonBadge(transaction.reason)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-xs text-gray-600 capitalize">
                                        {(transaction.source_type || 'n/a')} {'->'} {(transaction.destination_type || 'n/a')}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {(() => {
                                        const status = transaction.status || 'recorded';
                                        return (
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status === 'completed' ? 'bg-green-100 text-green-800' :
                                                status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        onClick={() => onViewDetails(transaction)}
                                        className="text-[#00A1DE] hover:text-[#0089bf]"
                                    >
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredTransactions.length === 0 && (
                <div className="px-6 py-12 text-center">
                    <p className="text-gray-500">No transactions found matching your filters</p>
                </div>
            )}
        </div>
    );
};

export default InventoryTransactionsTable;
